import {styled, css} from "@core-ui/react-stitch";
import slickEotFont from "slick-carousel/slick/fonts/slick.eot";
import slickWoffFont from "slick-carousel/slick/fonts/slick.woff";
import slickTtfFont from "slick-carousel/slick/fonts/slick.ttf";
import {reversePosition} from "../utils";
import {CarouselProps} from "../types";

type CarouselContainerProps = Required<
  Pick<CarouselProps, "fade" | "arrowPosition" | "arrowVerticalPosition">
>;

/**
 * This component incorporates CSS from Slick carousel
 * https://github.com/kenwheeler/slick/tree/master/slick
 * slick.css https://github.com/kenwheeler/slick/blob/v1.8.1/slick/slick.css
 * slick-theme.css https://github.com/kenwheeler/slick/blob/v1.8.1/slick/slick-theme.css
 * Below logic fixes fade option https://github.com/akiran/react-slick/issues/1167
 */
const CarouselContainer = styled.div<CarouselContainerProps>(
  ({fade, arrowPosition: arrowPositionProp, arrowVerticalPosition}) => {
    const arrowPosition = reversePosition(arrowPositionProp);

    return css`
      position: relative;
      ${fade && {
        ".slick-slide": {
          pointerEvents: "none",
        },
        ".slick-active": {
          pointerEvents: "auto",
        },
      }}
      /* slick.css */
    /* Slider */
    .slick-slider {
        position: relative;
        display: block;
        box-sizing: border-box;
        user-select: none;
        -webkit-touch-callout: none;
        touch-action: pan-y;
        -webkit-tap-highlight-color: transparent;
        scroll-behavior: smooth;

        .slick-track,
        .slick-list {
          transform: translate3d(0, 0, 0);
        }
      }

      .slick-list {
        position: relative;
        display: block;
        overflow-x: hidden;
        overflow-y: hidden;
        margin: 0;
        padding: 0;

        &:focus {
          outline: none;
        }

        &.dragging {
          cursor: pointer;
          cursor: hand;
        }
      }

      .slick-track {
        position: relative;
        top: 0;
        left: 0;
        display: block;
        margin-left: auto;
        margin-right: auto;

        &:before,
        &:after {
          display: table;
          content: "";
        }

        &:after {
          clear: both;
        }
      }

      .slick-slide {
        display: none;
        float: left;
        height: 100%;
        min-height: 1px;

        img {
          display: block;
        }

        &.slick-loading img {
          display: none;
        }

        &.dragging img {
          pointer-events: none;
        }
      }

      [dir="rtl"] .slick-slide {
        float: right;
      }

      .slick-initialized .slick-slide,
      .slick-vertical .slick-slide {
        display: block;
      }

      .slick-vertical .slick-slide {
        border: 1px solid transparent;
        height: auto;
      }

      .slick-loading {
        .slick-track,
        .slick-slide {
          visibility: hidden;
        }
        /* Slider */
        .slick-list {
          background: #fff url("./ajax-loader.gif") center center no-repeat;
        }
      }

      .slick-arrow.slick-hidden {
        display: none;
      }
      /* Icons */
      @font-face {
        font-family: "slick";
        font-weight: normal;
        font-style: normal;
        src: url(${slickEotFont});
        src: url(${slickEotFont}?#iefix) format("embedded-opentype"),
          url(${slickWoffFont}) format("woff"),
          url(${slickTtfFont}) format("truetype");
      }
      /* Arrows */
      .slick-prev,
      .slick-next {
        font-size: 0;
        line-height: 0;
        position: absolute;
        top: ${arrowVerticalPosition || "50%"};
        display: block;
        padding: 0;
        cursor: pointer;
        color: transparent;
        border: none;
        outline: none;
        background: transparent;

        &:hover,
        &:focus {
          color: transparent;
          outline: none;
          background: transparent;
          opacity: 1;
        }
        &.slick-disabled {
          opacity: 0.25;
        }
      }

      .slick-prev {
        left: ${arrowPosition};
        transform: translate(0, -50%) rotate(90deg);

        span {
          display: flex;
        }
      }

      [dir="rtl"] .slick-prev {
        right: ${arrowPosition};
        left: auto;
      }

      .slick-next {
        right: ${arrowPosition};
        transform: translate(0, -50%) rotate(-90deg);

        span {
          display: flex;
        }
      }

      [dir="rtl"] .slick-next {
        right: auto;
        left: ${arrowPosition};
      }

      /* Dots */
      .slick-dotted.slick-slider {
        margin-bottom: 30px;
      }

      .slick-dots {
        position: absolute;
        bottom: -25px;

        display: block;

        width: 100%;
        padding: 0;
        margin: 0;

        list-style: none;

        text-align: center;

        li {
          position: relative;
          display: inline-block;
          width: 20px;
          height: 20px;
          margin: 0 5px;
          padding: 0;
          cursor: pointer;

          &.slick-active {
            button {
              &:before {
                opacity: 0.75;
                color: black;
              }
            }
          }

          button {
            font-size: 0;
            line-height: 0;
            display: block;

            padding: 5px;
            cursor: pointer;
            color: transparent;
            border: 0;
            outline: none;
            background: transparent;

            &:hover,
            &:focus {
              outline: none;

              &:before,
              &:before {
                opacity: 1;
              }
            }

            &:before {
              font-family: "slick";
              font-size: 6px;
              line-height: 20px;
              position: absolute;
              top: 0;
              left: 0;
              width: 20px;
              height: 20px;
              content: "•";
              text-align: center;
              opacity: 0.25;
              color: black;
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
            }
          }
        }
      }
    `;
  }
);

export default CarouselContainer;
